import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import RangeSlider from '../common/RangeSlider';
import NoSoundHelp from '../common/NoSoundHelp';

import { ReactComponent as BassIcon } from '../../icons/bass-guitar.svg';
import { ReactComponent as CalloutIcon } from '../../icons/callout.svg';
import { ReactComponent as DrumIcon } from '../../icons/drum.svg';
import { ReactComponent as MetronomeIcon } from '../../icons/metronome-solid.svg';
import { ReactComponent as MusicIcon } from '../../icons/music-double-note.svg';
import { ReactComponent as MelodyIcon } from '../../icons/music-single-note.svg';
import { ReactComponent as PadIcon } from '../../icons/pad-wave.svg';
import { ReactComponent as AcousticIcon } from '../../icons/acoustic-guitar.svg';
import { ReactComponent as BrassIcon } from '../../icons/trumpet.svg';
import { ReactComponent as GuitarIcon } from '../../icons/electric-guitar.svg';
import { ReactComponent as ViolinIcon } from '../../icons/violin.svg';

import './ChordBarsLevels.scss';

const formatPercent = (value) => `${Math.round(value * 100)}%`;

// Each of the band's extra players, by BAND_LAYERS id, which also names its `player.band` text
const BAND_ICONS = {
  brass: BrassIcon,
  guitar: GuitarIcon,
  acoustic: AcousticIcon,
  violin: ViolinIcon,
};

/**
 * One sound's level. Given `mute`, its icon is the on/off switch for that sound; without one the
 * icon only says which sound this is. Given `cut`, the sound is held that share below where it is
 * set, and the part taken off is drawn.
 */
const Level = ({ id, label, icon, value, onChange, disabled = false, mute, cut = 0 }) => {
  const { t } = useTranslation();
  const isCut = cut > 0;
  const heldAt = value * (1 - cut);
  // A switched-off sound comes back on from a tap anywhere on its bar, not just its icon
  const canUnmute = mute && !mute.on && !mute.disabled;

  return (
    <div
      className={cx('level', { cut: isCut })}
      style={isCut ? { '--from': `${value * 100}%`, '--to': `${heldAt * 100}%` } : undefined}
    >
      {mute ? (
        <button
          type="button"
          className={cx('mute', { on: mute.on })}
          title={mute.title}
          aria-label={label}
          aria-pressed={mute.on}
          disabled={mute.disabled}
          onClick={mute.onToggle}
        >
          {icon}
        </button>
      ) : (
        <span className="level-icon">{icon}</span>
      )}
      <RangeSlider
        id={id}
        label={label}
        ariaLabel={t('player.levelVolume', { label })}
        value={isCut ? heldAt : value}
        onChange={(event) => onChange(Number(event.target.value))}
        step={0.05}
        formatValue={formatPercent}
        disabled={disabled || isCut}
      />
      {canUnmute && (
        // The icon is the accessible switch; this only widens its target
        <button type="button" className="unmute" tabIndex={-1} aria-hidden="true" onClick={mute.onToggle} />
      )}
      {isCut && (
        <>
          <span className="cut-gap" aria-hidden="true" />
          <span className="cut-badge">−{Math.round(cut * 100)}%</span>
        </>
      )}
    </div>
  );
};

Level.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  mute: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
    disabled: PropTypes.bool,
    title: PropTypes.string,
  }),
  cut: PropTypes.number,
};

/**
 * The preview's levels, stacked large so they are easy to drag on a phone, with "No sound?" and
 * Reset under them. Shown in the ChordBarsMixer sheet off a dialog's header, at the foot of the
 * Rhythm sheet, and in the intensity panel's own sheet. Holds no state.
 *
 * @param {object} player - From useChordBarsPreview or useMidiPreview. Melody shows only when the
 *   player has one, Bass only when the style playing has a bass player, Pad only when a part plays
 *   a style with a pad at a level that brings it in, and Voice only when it has parts to call.
 *   Reset shows where the player has `resetMix`. Effects belong to the intensity panel, never here.
 * @param {string} idPrefix - Namespaces the sliders' ids
 * @param {node} instrumentIcon - Icon for the instrument level; defaults to the chords' icon
 * @param {boolean} drums - Adds the drums level, for the dialogs that play a Rhythm
 */
const ChordBarsLevels = ({ player, idPrefix = 'chord-bars', instrumentIcon, drums = false }) => {
  const { t } = useTranslation();

  return (
    <div className="ChordBarsLevels">
      <div className="levels">
        <Level
          id={`${idPrefix}-instrument-volume`}
          label={t('player.instrument')}
          icon={instrumentIcon || <MusicIcon className="stroke-icon" aria-hidden="true" />}
          value={player.instrumentVolume}
          onChange={player.setInstrumentVolume}
          cut={player.chordsCut}
        />
        <Level
          id={`${idPrefix}-click-volume`}
          label={t('player.click')}
          icon={<MetronomeIcon aria-hidden="true" />}
          value={player.clickVolume}
          onChange={player.setClickVolume}
          disabled={!player.metronome}
          mute={{ on: player.metronome, onToggle: () => player.setMetronome((on) => !on), title: t('player.click') }}
        />
        {drums && (
          <Level
            id={`${idPrefix}-drum-volume`}
            label={t('player.drums')}
            icon={<DrumIcon className="stroke-icon" aria-hidden="true" />}
            value={player.drumVolume}
            onChange={player.setDrumVolume}
            disabled={!player.drums}
            mute={{
              on: player.drums,
              onToggle: player.toggleDrums,
              title: t('player.drumsHint'),
            }}
          />
        )}
        {player.hasBass && (
          <Level
            id={`${idPrefix}-bass-volume`}
            label={t('player.bass')}
            icon={<BassIcon className="stroke-icon" aria-hidden="true" />}
            value={player.bassVolume}
            onChange={player.setBassVolume}
            disabled={!player.bassOn}
            // Off hands the low end back to the piano's left hand, so it waits for Stop like the melody
            mute={{
              on: player.bassOn,
              onToggle: player.toggleBass,
              disabled: Boolean(player.isPlaying || player.isLoading),
              title: t('player.bassHint'),
            }}
          />
        )}
        {player.hasPad && (
          <Level
            id={`${idPrefix}-pad-volume`}
            label={t('player.pad')}
            icon={<PadIcon className="stroke-icon" aria-hidden="true" />}
            value={player.padVolume}
            onChange={player.setPadVolume}
            disabled={!player.padOn}
            // Changes the notes scheduled, so like the bass it waits for Stop
            mute={{
              on: player.padOn,
              onToggle: player.togglePad,
              disabled: Boolean(player.isPlaying || player.isLoading),
              title: t('player.padHint'),
            }}
          />
        )}
        {(player.band || [])
          .filter((layer) => layer.has)
          .map(({ id, name, on, toggle, volume, setVolume }) => {
            const Icon = BAND_ICONS[id];
            const title = t(`player.band.${id}Hint`);

            return (
              <Level
                key={id}
                id={`${idPrefix}-${id}-volume`}
                label={t(`player.band.${id}`, { defaultValue: name })}
                icon={<Icon className="stroke-icon" aria-hidden="true" />}
                value={volume}
                onChange={setVolume}
                disabled={!on}
                // Changes the notes scheduled, so like the pad it waits for Stop
                mute={{ on, onToggle: toggle, disabled: Boolean(player.isPlaying || player.isLoading), title }}
              />
            );
          })}
        {player.hasMelody && (
          <Level
            id={`${idPrefix}-melody-volume`}
            label={t('player.melody')}
            icon={<MelodyIcon className="stroke-icon" aria-hidden="true" />}
            value={player.melodyVolume}
            onChange={player.setMelodyVolume}
            disabled={!player.melodyOn}
            // Unlike the other mutes this changes the notes scheduled, so it waits for Stop
            mute={{
              on: player.melodyOn,
              onToggle: player.toggleMelody,
              disabled: Boolean(player.isPlaying || player.isLoading),
              title: t('player.melodyHint'),
            }}
          />
        )}
        {/* Switched here or by Call parts, and heard live like the click */}
        {player.hasCallouts && (
          <Level
            id={`${idPrefix}-callout-volume`}
            label={t('player.voice')}
            icon={<CalloutIcon className="stroke-icon" aria-hidden="true" />}
            value={player.calloutVolume}
            onChange={player.setCalloutVolume}
            disabled={!player.calloutsOn}
            mute={{
              on: player.calloutsOn,
              onToggle: player.toggleCallouts,
              title: t('player.callPartsHint'),
            }}
          />
        )}
      </div>

      <NoSoundHelp
        hasVolumeSliders
        action={
          player.resetMix && (
            <button
              type="button"
              title={t('player.resetLevelsHint')}
              disabled={!player.canResetMix}
              onClick={player.resetMix}
            >
              {t('player.resetLevels')}
            </button>
          )
        }
      />
    </div>
  );
};

ChordBarsLevels.propTypes = {
  player: PropTypes.shape({
    metronome: PropTypes.bool,
    setMetronome: PropTypes.func,
    clickVolume: PropTypes.number,
    setClickVolume: PropTypes.func,
    instrumentVolume: PropTypes.number,
    setInstrumentVolume: PropTypes.func,
    chordsCut: PropTypes.number,
    drums: PropTypes.bool,
    toggleDrums: PropTypes.func,
    drumVolume: PropTypes.number,
    setDrumVolume: PropTypes.func,
    hasMelody: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isLoading: PropTypes.bool,
    melodyOn: PropTypes.bool,
    toggleMelody: PropTypes.func,
    melodyVolume: PropTypes.number,
    setMelodyVolume: PropTypes.func,
    hasBass: PropTypes.bool,
    bassOn: PropTypes.bool,
    toggleBass: PropTypes.func,
    bassVolume: PropTypes.number,
    setBassVolume: PropTypes.func,
    hasPad: PropTypes.bool,
    padOn: PropTypes.bool,
    togglePad: PropTypes.func,
    padVolume: PropTypes.number,
    setPadVolume: PropTypes.func,
    band: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        has: PropTypes.bool,
        on: PropTypes.bool,
        toggle: PropTypes.func,
        volume: PropTypes.number,
        setVolume: PropTypes.func,
      }),
    ),
    hasCallouts: PropTypes.bool,
    calloutsOn: PropTypes.bool,
    toggleCallouts: PropTypes.func,
    calloutVolume: PropTypes.number,
    setCalloutVolume: PropTypes.func,
    canResetMix: PropTypes.bool,
    resetMix: PropTypes.func,
  }).isRequired,
  idPrefix: PropTypes.string,
  instrumentIcon: PropTypes.node,
  drums: PropTypes.bool,
};

export default ChordBarsLevels;
